import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI } from '../../services/api';
import Navbar from '../../components/Navbar';
import './AddonManagement.css';

function AddonManagement() {
  const [activeTab, setActiveTab] = useState('types');
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Addon Types State
  const [addons, setAddons] = useState([]);
  const [showAddonForm, setShowAddonForm] = useState(false);
  const [editingAddon, setEditingAddon] = useState(null);
  const [addonForm, setAddonForm] = useState({
    name: '',
    slug: 'search_priority',
    description: '',
    displayName: '',
    icon: '',
    scope: 'city_wise',
    pricing: {
      monthly: { enabled: true, basePrice: 0, cityPricing: [] },
      oneTime: { enabled: false, basePrice: 0, durationDays: 30, cityPricing: [] }
    },
    config: {
      searchBoostFactor: 2.0,
      bannerPosition: 1,
      maxFeaturedPerCity: 10,
      badgeText: 'Promoted',
      badgeColor: '#FF6B6B',
      deliveryPriorityBoost: 1.5
    },
    sortOrder: 0
  });

  // Assignments State
  const [assignments, setAssignments] = useState([]);
  const [assignmentFilters, setAssignmentFilters] = useState({
    status: '',
    addonSlug: '',
    page: 1
  });
  const [assignmentPagination, setAssignmentPagination] = useState({ total: 0, pages: 1 });
  const [showAssignForm, setShowAssignForm] = useState(false);
  const [cities, setCities] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [restaurantSearch, setRestaurantSearch] = useState('');
  const [assignForm, setAssignForm] = useState({
    restaurantId: '',
    addonId: '',
    subscriptionType: 'monthly',
    isGlobal: false,
    durationDays: 30,
    cities: [],
    notes: ''
  });

  // Stats State
  const [stats, setStats] = useState(null);

  // Addon Requests State (from restaurants)
  const [addonRequests, setAddonRequests] = useState([]);
  const [requestFilters, setRequestFilters] = useState({
    status: '',
    addonSlug: '',
    page: 1
  });
  const [requestPagination, setRequestPagination] = useState({ total: 0, pages: 1 });
  const [processingRequest, setProcessingRequest] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  // Fetch Addon Types
  const fetchAddons = useCallback(async () => {
    try {
      const response = await adminAPI.getAllAddons();
      if (response.data.success) {
        setAddons(response.data.addons);
      }
    } catch (error) {
      console.error('Error fetching addons:', error);
      showMessage('error', 'Failed to fetch addons');
    }
  }, []);

  // Fetch Assignments
  const fetchAssignments = useCallback(async () => {
    try {
      const response = await adminAPI.getAllAddonAssignments(assignmentFilters);
      if (response.data.success) {
        setAssignments(response.data.assignments);
        setAssignmentPagination(response.data.pagination);
      }
    } catch (error) {
      console.error('Error fetching assignments:', error);
      showMessage('error', 'Failed to fetch assignments');
    }
  }, [assignmentFilters]);

  // Fetch Stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await adminAPI.getAddonStats();
      if (response.data.success) {
        setStats(response.data.stats);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  // Fetch Addon Requests (from restaurants)
  const fetchAddonRequests = useCallback(async () => {
    try {
      const response = await adminAPI.getPendingAddonRequests(requestFilters);
      if (response.data.success) {
        setAddonRequests(response.data.requests || []);
        setRequestPagination(response.data.pagination || { total: 0, pages: 1 });
      }
    } catch (error) {
      console.error('Error fetching addon requests:', error);
      showMessage('error', 'Failed to fetch addon requests');
    }
  }, [requestFilters]);

  // Approve Addon Request
  const handleApproveRequest = async (requestId) => {
    setProcessingRequest(requestId);
    try {
      const response = await adminAPI.approveAddonRequest(requestId);
      if (response.data.success) {
        showMessage('success', 'Addon request approved and assigned successfully');
        fetchAddonRequests();
        fetchAssignments();
      } else {
        showMessage('error', response.data.message || 'Failed to approve request');
      }
    } catch (error) {
      console.error('Error approving request:', error);
      showMessage('error', error.response?.data?.message || 'Failed to approve request');
    } finally {
      setProcessingRequest(null);
    }
  };

  // Reject Addon Request
  const handleRejectRequest = async () => {
    if (!selectedRequest) return;
    setProcessingRequest(selectedRequest._id);
    try {
      const response = await adminAPI.rejectAddonRequest(selectedRequest._id, {
        reason: rejectReason,
        refund: true
      });
      if (response.data.success) {
        showMessage('success', 'Addon request rejected');
        fetchAddonRequests();
        setShowRejectModal(false);
        setRejectReason('');
        setSelectedRequest(null);
      } else {
        showMessage('error', response.data.message || 'Failed to reject request');
      }
    } catch (error) {
      console.error('Error rejecting request:', error);
      showMessage('error', error.response?.data?.message || 'Failed to reject request');
    } finally {
      setProcessingRequest(null);
    }
  };

  // Fetch Cities
  const fetchCities = async () => {
    try {
      const response = await adminAPI.getCitiesForAddon();
      if (response.data.success) {
        setCities(response.data.cities);
      }
    } catch (error) {
      console.error('Error fetching cities:', error);
    }
  };

  // Fetch Restaurants (for assignment)
  const fetchRestaurants = async (search = '') => {
    try {
      const response = await adminAPI.getRestaurantsForAddon({ search, limit: 20 });
      if (response.data.success) {
        setRestaurants(response.data.restaurants);
      }
    } catch (error) {
      console.error('Error fetching restaurants:', error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchAddons();
      await fetchStats();
      await fetchCities();
      setLoading(false);
    };
    loadData();
  }, [fetchAddons, fetchStats]);

  useEffect(() => {
    if (activeTab === 'assignments') {
      fetchAssignments();
    }
  }, [activeTab, fetchAssignments]);

  useEffect(() => {
    if (activeTab === 'requests') {
      fetchAddonRequests();
    }
  }, [activeTab, fetchAddonRequests]);

  // Seed Default Addons
  const handleSeedAddons = async () => {
    if (!window.confirm('This will create default addon types. Continue?')) return;
    try {
      const response = await adminAPI.seedDefaultAddons();
      if (response.data.success) {
        showMessage('success', 'Default addons created successfully');
        fetchAddons();
      }
    } catch (error) {
      showMessage('error', error.response?.data?.message || 'Failed to seed addons');
    }
  };

  // Create/Update Addon
  const handleSaveAddon = async (e) => {
    e.preventDefault();
    try {
      let response;
      if (editingAddon) {
        response = await adminAPI.updateAddon(editingAddon._id, addonForm);
      } else {
        response = await adminAPI.createAddon(addonForm);
      }
      if (response.data.success) {
        showMessage('success', `Addon ${editingAddon ? 'updated' : 'created'} successfully`);
        setShowAddonForm(false);
        setEditingAddon(null);
        resetAddonForm();
        fetchAddons();
      }
    } catch (error) {
      showMessage('error', error.response?.data?.message || 'Failed to save addon');
    }
  };

  // Toggle Addon Status
  const handleToggleAddon = async (addonId) => {
    try {
      const response = await adminAPI.toggleAddonStatus(addonId);
      if (response.data.success) {
        showMessage('success', response.data.message);
        fetchAddons();
      }
    } catch (error) {
      showMessage('error', error.response?.data?.message || 'Failed to toggle addon');
    }
  };

  // Delete Addon
  const handleDeleteAddon = async (addonId) => {
    if (!window.confirm('Are you sure you want to delete this addon?')) return;
    try {
      const response = await adminAPI.deleteAddon(addonId);
      if (response.data.success) {
        showMessage('success', 'Addon deleted successfully');
        fetchAddons();
      }
    } catch (error) {
      showMessage('error', error.response?.data?.message || 'Failed to delete addon');
    }
  };

  // Edit Addon
  const handleEditAddon = (addon) => {
    setEditingAddon(addon);
    setAddonForm({
      name: addon.name,
      slug: addon.slug,
      description: addon.description,
      displayName: addon.displayName,
      icon: addon.icon || '',
      scope: addon.scope,
      pricing: addon.pricing,
      config: addon.config,
      sortOrder: addon.sortOrder
    });
    setShowAddonForm(true);
  };

  const resetAddonForm = () => {
    setAddonForm({
      name: '',
      slug: 'search_priority',
      description: '',
      displayName: '',
      icon: '',
      scope: 'city_wise',
      pricing: {
        monthly: { enabled: true, basePrice: 0, cityPricing: [] },
        oneTime: { enabled: false, basePrice: 0, durationDays: 30, cityPricing: [] }
      },
      config: {
        searchBoostFactor: 2.0,
        bannerPosition: 1,
        maxFeaturedPerCity: 10,
        badgeText: 'Promoted',
        badgeColor: '#FF6B6B',
        deliveryPriorityBoost: 1.5
      },
      sortOrder: 0
    });
  };

  // Assign Addon to Restaurant
  const handleAssignAddon = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!assignForm.restaurantId || assignForm.restaurantId.trim() === '') {
      showMessage('error', 'Please select a restaurant');
      return;
    }
    if (!assignForm.addonId || assignForm.addonId.trim() === '') {
      showMessage('error', 'Please select an addon');
      return;
    }

    try {
      const response = await adminAPI.assignAddonToRestaurant(assignForm);
      if (response.data.success) {
        showMessage('success', 'Addon assigned successfully');
        setShowAssignForm(false);
        resetAssignForm();
        fetchAssignments();
        fetchStats();
      }
    } catch (error) {
      showMessage('error', error.response?.data?.message || 'Failed to assign addon');
    }
  };

  // Cancel Assignment
  const handleCancelAssignment = async (assignmentId) => {
    const reason = window.prompt('Enter cancellation reason:');
    if (!reason) return;
    try {
      const response = await adminAPI.cancelAddonAssignment(assignmentId, reason);
      if (response.data.success) {
        showMessage('success', 'Assignment cancelled');
        fetchAssignments();
        fetchStats();
      }
    } catch (error) {
      showMessage('error', error.response?.data?.message || 'Failed to cancel assignment');
    }
  };

  // Renew Assignment
  const handleRenewAssignment = async (assignmentId) => {
    const daysInput = window.prompt('Enter renewal duration (days):', '30');
    if (!daysInput) return;
    const durationDays = parseInt(daysInput);
    if (isNaN(durationDays) || durationDays <= 0) {
      showMessage('error', 'Invalid duration');
      return;
    }
    try {
      const response = await adminAPI.renewAddonAssignment(assignmentId, { durationDays });
      if (response.data.success) {
        showMessage('success', 'Assignment renewed successfully');
        fetchAssignments();
        fetchStats();
      }
    } catch (error) {
      showMessage('error', error.response?.data?.message || 'Failed to renew assignment');
    }
  };

  const resetAssignForm = () => {
    setAssignForm({
      restaurantId: '',
      addonId: '',
      subscriptionType: 'monthly',
      isGlobal: false,
      durationDays: 30,
      cities: [],
      notes: ''
    });
    setRestaurantSearch('');
  };

  const handleRestaurantSearch = (e) => {
    const value = e.target.value;
    setRestaurantSearch(value);
    if (value.length >= 2) {
      fetchRestaurants(value);
    }
  };

  const getAddonIcon = (slug) => {
    const icons = {
      search_priority: '🔍',
      featured_banner: '⭐',
      promoted_badge: '🏷️',
      priority_delivery: '🚀'
    };
    return icons[slug] || '📦';
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: { bg: '#e8f5e9', color: '#2e7d32' },
      expired: { bg: '#fce4ec', color: '#c62828' },
      cancelled: { bg: '#f5f5f5', color: '#757575' },
      pending_payment: { bg: '#fff3e0', color: '#e65100' }
    };
    const style = styles[status] || styles.cancelled;
    return (
      <span style={{
        background: style.bg,
        color: style.color,
        padding: '4px 10px',
        borderRadius: '20px',
        fontSize: '0.75rem',
        fontWeight: 600,
        textTransform: 'uppercase'
      }}>
        {status.replace('_', ' ')}
      </span>
    );
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="addon-container">
          <div className="loading">Loading addon management...</div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="addon-container">
        <h1>Subscription Addon Management</h1>

        {message.text && (
          <div className={`message ${message.type}`}>
            {message.text}
          </div>
        )}

        {/* Tabs */}
        <div className="tabs">
          <button
            className={`tab ${activeTab === 'types' ? 'active' : ''}`}
            onClick={() => setActiveTab('types')}
          >
            Addon Types
          </button>
          <button
            className={`tab ${activeTab === 'assignments' ? 'active' : ''}`}
            onClick={() => setActiveTab('assignments')}
          >
            Assignments
          </button>
          <button
            className={`tab ${activeTab === 'requests' ? 'active' : ''}`}
            onClick={() => setActiveTab('requests')}
          >
            Requests {addonRequests.filter(r => r.status === 'pending_approval').length > 0 &&
              <span className="badge">{addonRequests.filter(r => r.status === 'pending_approval').length}</span>
            }
          </button>
          <button
            className={`tab ${activeTab === 'stats' ? 'active' : ''}`}
            onClick={() => { setActiveTab('stats'); fetchStats(); }}
          >
            Statistics
          </button>
        </div>

        {/* Addon Types Tab */}
        {activeTab === 'types' && (
          <div className="tab-content">
            <div className="addon-card actions-card">
              <div className="actions-row">
                <button
                  className="btn btn-primary"
                  onClick={() => { setShowAddonForm(true); setEditingAddon(null); resetAddonForm(); }}
                >
                  + Create Addon Type
                </button>
                {addons.length === 0 && (
                  <button className="btn btn-secondary" onClick={handleSeedAddons}>
                    Seed Default Addons
                  </button>
                )}
              </div>
            </div>

            {/* Addon Form */}
            {showAddonForm && (
              <div className="addon-card form-card">
                <h2>{editingAddon ? 'Edit Addon Type' : 'Create New Addon Type'}</h2>
                <form onSubmit={handleSaveAddon}>
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Name *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={addonForm.name}
                        onChange={(e) => setAddonForm({ ...addonForm, name: e.target.value })}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Slug *</label>
                      <select
                        className="form-control"
                        value={addonForm.slug}
                        onChange={(e) => setAddonForm({ ...addonForm, slug: e.target.value })}
                        disabled={!!editingAddon}
                      >
                        <option value="search_priority">search_priority</option>
                        <option value="featured_banner">featured_banner</option>
                        <option value="promoted_badge">promoted_badge</option>
                        <option value="priority_delivery">priority_delivery</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Display Name *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={addonForm.displayName}
                        onChange={(e) => setAddonForm({ ...addonForm, displayName: e.target.value })}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Icon (emoji)</label>
                      <input
                        type="text"
                        className="form-control"
                        value={addonForm.icon}
                        onChange={(e) => setAddonForm({ ...addonForm, icon: e.target.value })}
                        placeholder="e.g. 🔍"
                      />
                    </div>
                    <div className="form-group full-width">
                      <label>Description *</label>
                      <textarea
                        className="form-control"
                        value={addonForm.description}
                        onChange={(e) => setAddonForm({ ...addonForm, description: e.target.value })}
                        required
                        rows={2}
                      />
                    </div>
                    <div className="form-group">
                      <label>Scope *</label>
                      <select
                        className="form-control"
                        value={addonForm.scope}
                        onChange={(e) => setAddonForm({ ...addonForm, scope: e.target.value })}
                      >
                        <option value="city_wise">City-wise</option>
                        <option value="global">Global</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Sort Order</label>
                      <input
                        type="number"
                        className="form-control"
                        value={addonForm.sortOrder}
                        onChange={(e) => setAddonForm({ ...addonForm, sortOrder: parseInt(e.target.value) || 0 })}
                      />
                    </div>
                  </div>

                  {/* Pricing Section */}
                  <h3 style={{ marginTop: '20px' }}>Pricing</h3>
                  <div className="pricing-section">
                    <div className="pricing-type">
                      <label>
                        <input
                          type="checkbox"
                          checked={addonForm.pricing.monthly.enabled}
                          onChange={(e) => setAddonForm({
                            ...addonForm,
                            pricing: {
                              ...addonForm.pricing,
                              monthly: { ...addonForm.pricing.monthly, enabled: e.target.checked }
                            }
                          })}
                        />
                        Monthly Subscription
                      </label>
                      {addonForm.pricing.monthly.enabled && (
                        <>
                          <div className="form-group">
                            <label>Base Price (INR) {addonForm.scope === 'city_wise' ? '(Default)' : ''}</label>
                            <input
                              type="number"
                              className="form-control"
                              value={addonForm.pricing.monthly.basePrice}
                              onChange={(e) => setAddonForm({
                                ...addonForm,
                                pricing: {
                                  ...addonForm.pricing,
                                  monthly: { ...addonForm.pricing.monthly, basePrice: parseFloat(e.target.value) || 0 }
                                }
                              })}
                            />
                          </div>
                          {/* City-wise Pricing for Monthly */}
                          {addonForm.scope === 'city_wise' && (
                            <div className="city-pricing-section">
                              <label className="city-pricing-header">
                                <span>City-wise Pricing (Monthly)</span>
                                <small>Leave empty to use base price</small>
                              </label>
                              <div className="city-pricing-grid">
                                {cities.map(city => {
                                  const existingPrice = addonForm.pricing.monthly.cityPricing.find(
                                    cp => cp.cityId === city._id || cp.cityName === city.name
                                  );
                                  return (
                                    <div key={city._id} className="city-price-item">
                                      <span className="city-name">{city.name}</span>
                                      <input
                                        type="number"
                                        className="form-control city-price-input"
                                        placeholder={`₹${addonForm.pricing.monthly.basePrice}`}
                                        value={existingPrice?.price || ''}
                                        onChange={(e) => {
                                          const newPrice = parseFloat(e.target.value);
                                          let updatedCityPricing = [...addonForm.pricing.monthly.cityPricing];
                                          const existingIndex = updatedCityPricing.findIndex(
                                            cp => cp.cityId === city._id || cp.cityName === city.name
                                          );

                                          if (e.target.value === '' || isNaN(newPrice)) {
                                            // Remove city pricing if empty
                                            if (existingIndex > -1) {
                                              updatedCityPricing.splice(existingIndex, 1);
                                            }
                                          } else {
                                            if (existingIndex > -1) {
                                              updatedCityPricing[existingIndex] = {
                                                cityId: city._id,
                                                cityName: city.name,
                                                price: newPrice
                                              };
                                            } else {
                                              updatedCityPricing.push({
                                                cityId: city._id,
                                                cityName: city.name,
                                                price: newPrice
                                              });
                                            }
                                          }

                                          setAddonForm({
                                            ...addonForm,
                                            pricing: {
                                              ...addonForm.pricing,
                                              monthly: {
                                                ...addonForm.pricing.monthly,
                                                cityPricing: updatedCityPricing
                                              }
                                            }
                                          });
                                        }}
                                      />
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                    <div className="pricing-type">
                      <label>
                        <input
                          type="checkbox"
                          checked={addonForm.pricing.oneTime.enabled}
                          onChange={(e) => setAddonForm({
                            ...addonForm,
                            pricing: {
                              ...addonForm.pricing,
                              oneTime: { ...addonForm.pricing.oneTime, enabled: e.target.checked }
                            }
                          })}
                        />
                        One-Time Purchase
                      </label>
                      {addonForm.pricing.oneTime.enabled && (
                        <>
                          <div className="form-group">
                            <label>Base Price (INR) {addonForm.scope === 'city_wise' ? '(Default)' : ''}</label>
                            <input
                              type="number"
                              className="form-control"
                              value={addonForm.pricing.oneTime.basePrice}
                              onChange={(e) => setAddonForm({
                                ...addonForm,
                                pricing: {
                                  ...addonForm.pricing,
                                  oneTime: { ...addonForm.pricing.oneTime, basePrice: parseFloat(e.target.value) || 0 }
                                }
                              })}
                            />
                          </div>
                          <div className="form-group">
                            <label>Duration (days)</label>
                            <input
                              type="number"
                              className="form-control"
                              value={addonForm.pricing.oneTime.durationDays}
                              onChange={(e) => setAddonForm({
                                ...addonForm,
                                pricing: {
                                  ...addonForm.pricing,
                                  oneTime: { ...addonForm.pricing.oneTime, durationDays: parseInt(e.target.value) || 30 }
                                }
                              })}
                            />
                          </div>
                          {/* City-wise Pricing for One-Time */}
                          {addonForm.scope === 'city_wise' && (
                            <div className="city-pricing-section">
                              <label className="city-pricing-header">
                                <span>City-wise Pricing (One-Time)</span>
                                <small>Leave empty to use base price</small>
                              </label>
                              <div className="city-pricing-grid">
                                {cities.map(city => {
                                  const existingPrice = addonForm.pricing.oneTime.cityPricing.find(
                                    cp => cp.cityId === city._id || cp.cityName === city.name
                                  );
                                  return (
                                    <div key={city._id} className="city-price-item">
                                      <span className="city-name">{city.name}</span>
                                      <input
                                        type="number"
                                        className="form-control city-price-input"
                                        placeholder={`₹${addonForm.pricing.oneTime.basePrice}`}
                                        value={existingPrice?.price || ''}
                                        onChange={(e) => {
                                          const newPrice = parseFloat(e.target.value);
                                          let updatedCityPricing = [...addonForm.pricing.oneTime.cityPricing];
                                          const existingIndex = updatedCityPricing.findIndex(
                                            cp => cp.cityId === city._id || cp.cityName === city.name
                                          );

                                          if (e.target.value === '' || isNaN(newPrice)) {
                                            // Remove city pricing if empty
                                            if (existingIndex > -1) {
                                              updatedCityPricing.splice(existingIndex, 1);
                                            }
                                          } else {
                                            if (existingIndex > -1) {
                                              updatedCityPricing[existingIndex] = {
                                                cityId: city._id,
                                                cityName: city.name,
                                                price: newPrice
                                              };
                                            } else {
                                              updatedCityPricing.push({
                                                cityId: city._id,
                                                cityName: city.name,
                                                price: newPrice
                                              });
                                            }
                                          }

                                          setAddonForm({
                                            ...addonForm,
                                            pricing: {
                                              ...addonForm.pricing,
                                              oneTime: {
                                                ...addonForm.pricing.oneTime,
                                                cityPricing: updatedCityPricing
                                              }
                                            }
                                          });
                                        }}
                                      />
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  {/* Config Section */}
                  <h3 style={{ marginTop: '20px' }}>Configuration</h3>
                  <div className="form-grid">
                    {addonForm.slug === 'search_priority' && (
                      <div className="form-group">
                        <label>Search Boost Factor (1.0-10.0)</label>
                        <input
                          type="number"
                          className="form-control"
                          step="0.1"
                          min="1"
                          max="10"
                          value={addonForm.config.searchBoostFactor}
                          onChange={(e) => setAddonForm({
                            ...addonForm,
                            config: { ...addonForm.config, searchBoostFactor: parseFloat(e.target.value) || 2.0 }
                          })}
                        />
                      </div>
                    )}
                    {addonForm.slug === 'featured_banner' && (
                      <>
                        <div className="form-group">
                          <label>Banner Position</label>
                          <input
                            type="number"
                            className="form-control"
                            value={addonForm.config.bannerPosition}
                            onChange={(e) => setAddonForm({
                              ...addonForm,
                              config: { ...addonForm.config, bannerPosition: parseInt(e.target.value) || 1 }
                            })}
                          />
                        </div>
                        <div className="form-group">
                          <label>Max Featured Per City</label>
                          <input
                            type="number"
                            className="form-control"
                            value={addonForm.config.maxFeaturedPerCity}
                            onChange={(e) => setAddonForm({
                              ...addonForm,
                              config: { ...addonForm.config, maxFeaturedPerCity: parseInt(e.target.value) || 10 }
                            })}
                          />
                        </div>
                      </>
                    )}
                    {addonForm.slug === 'promoted_badge' && (
                      <>
                        <div className="form-group">
                          <label>Badge Text</label>
                          <input
                            type="text"
                            className="form-control"
                            value={addonForm.config.badgeText}
                            onChange={(e) => setAddonForm({
                              ...addonForm,
                              config: { ...addonForm.config, badgeText: e.target.value }
                            })}
                          />
                        </div>
                        <div className="form-group">
                          <label>Badge Color</label>
                          <input
                            type="color"
                            className="form-control"
                            value={addonForm.config.badgeColor}
                            onChange={(e) => setAddonForm({
                              ...addonForm,
                              config: { ...addonForm.config, badgeColor: e.target.value }
                            })}
                          />
                        </div>
                      </>
                    )}
                    {addonForm.slug === 'priority_delivery' && (
                      <div className="form-group">
                        <label>Priority Boost Factor (1.0-5.0)</label>
                        <input
                          type="number"
                          className="form-control"
                          step="0.1"
                          min="1"
                          max="5"
                          value={addonForm.config.deliveryPriorityBoost}
                          onChange={(e) => setAddonForm({
                            ...addonForm,
                            config: { ...addonForm.config, deliveryPriorityBoost: parseFloat(e.target.value) || 1.5 }
                          })}
                        />
                      </div>
                    )}
                  </div>

                  <div className="form-actions">
                    <button type="submit" className="btn btn-primary">
                      {editingAddon ? 'Update Addon' : 'Create Addon'}
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => { setShowAddonForm(false); setEditingAddon(null); }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Addon List */}
            <div className="addon-card">
              <h2>Addon Types ({addons.length})</h2>
              {addons.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">📦</div>
                  <p>No addon types found. Create your first addon or seed defaults.</p>
                </div>
              ) : (
                <div className="addon-list">
                  {addons.map(addon => (
                    <div key={addon._id} className={`addon-item ${addon.isActive ? 'active' : 'inactive'}`}>
                      <div className="addon-main">
                        <div className="addon-header">
                          <span className="addon-icon">{addon.icon || getAddonIcon(addon.slug)}</span>
                          <span className="addon-name">{addon.displayName}</span>
                          <span className={`badge ${addon.isActive ? 'badge-active' : 'badge-inactive'}`}>
                            {addon.isActive ? 'Active' : 'Inactive'}
                          </span>
                          <span className="badge badge-scope">{addon.scope}</span>
                        </div>
                        <p className="addon-description">{addon.description}</p>
                        <div className="addon-details">
                          <div className="detail-item">
                            <span className="detail-label">Slug</span>
                            <span className="detail-value">{addon.slug}</span>
                          </div>
                          {addon.pricing.monthly.enabled && (
                            <div className="detail-item">
                              <span className="detail-label">Monthly Price</span>
                              <span className="detail-value">
                                ₹{addon.pricing.monthly.basePrice}
                                {addon.scope === 'city_wise' && addon.pricing.monthly.cityPricing?.length > 0 && (
                                  <span className="city-count-badge">
                                    +{addon.pricing.monthly.cityPricing.length} cities
                                  </span>
                                )}
                              </span>
                            </div>
                          )}
                          {addon.pricing.oneTime.enabled && (
                            <div className="detail-item">
                              <span className="detail-label">One-Time Price</span>
                              <span className="detail-value">
                                ₹{addon.pricing.oneTime.basePrice} ({addon.pricing.oneTime.durationDays} days)
                                {addon.scope === 'city_wise' && addon.pricing.oneTime.cityPricing?.length > 0 && (
                                  <span className="city-count-badge">
                                    +{addon.pricing.oneTime.cityPricing.length} cities
                                  </span>
                                )}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="addon-actions">
                        <button className="btn btn-sm btn-secondary" onClick={() => handleEditAddon(addon)}>
                          Edit
                        </button>
                        <button
                          className={`btn btn-sm ${addon.isActive ? 'btn-warning' : 'btn-success'}`}
                          onClick={() => handleToggleAddon(addon._id)}
                        >
                          {addon.isActive ? 'Deactivate' : 'Activate'}
                        </button>
                        <button className="btn btn-sm btn-danger" onClick={() => handleDeleteAddon(addon._id)}>
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Assignments Tab */}
        {activeTab === 'assignments' && (
          <div className="tab-content">
            <div className="addon-card actions-card">
              <div className="actions-row">
                <button
                  className="btn btn-primary"
                  onClick={() => { setShowAssignForm(true); resetAssignForm(); fetchRestaurants(''); }}
                >
                  + Assign Addon to Restaurant
                </button>
                <div className="filters-row">
                  <div className="filter-group">
                    <label>Status:</label>
                    <select
                      className="filter-select"
                      value={assignmentFilters.status}
                      onChange={(e) => setAssignmentFilters({ ...assignmentFilters, status: e.target.value, page: 1 })}
                    >
                      <option value="">All</option>
                      <option value="active">Active</option>
                      <option value="expired">Expired</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                  <div className="filter-group">
                    <label>Addon:</label>
                    <select
                      className="filter-select"
                      value={assignmentFilters.addonSlug}
                      onChange={(e) => setAssignmentFilters({ ...assignmentFilters, addonSlug: e.target.value, page: 1 })}
                    >
                      <option value="">All</option>
                      <option value="search_priority">Search Priority</option>
                      <option value="featured_banner">Featured Banner</option>
                      <option value="promoted_badge">Promoted Badge</option>
                      <option value="priority_delivery">Priority Delivery</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Assignment Form */}
            {showAssignForm && (
              <div className="addon-card form-card">
                <h2>Assign Addon to Restaurant</h2>
                <form onSubmit={handleAssignAddon}>
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Search Restaurant *</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Type restaurant name..."
                        value={restaurantSearch}
                        onChange={handleRestaurantSearch}
                      />
                      {restaurants.length > 0 && (
                        <select
                          className="form-control"
                          style={{ marginTop: '5px' }}
                          value={assignForm.restaurantId}
                          onChange={(e) => setAssignForm({ ...assignForm, restaurantId: e.target.value })}
                          required
                        >
                          <option value="">Select a restaurant</option>
                          {restaurants.map(r => (
                            <option key={r._id} value={r._id}>
                              {r.name} - {r.address?.city || 'N/A'}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                    <div className="form-group">
                      <label>Addon Type *</label>
                      <select
                        className="form-control"
                        value={assignForm.addonId}
                        onChange={(e) => setAssignForm({ ...assignForm, addonId: e.target.value })}
                        required
                      >
                        <option value="">Select addon</option>
                        {addons.filter(a => a.isActive).map(a => (
                          <option key={a._id} value={a._id}>
                            {a.icon || getAddonIcon(a.slug)} {a.displayName}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Subscription Type *</label>
                      <select
                        className="form-control"
                        value={assignForm.subscriptionType}
                        onChange={(e) => setAssignForm({ ...assignForm, subscriptionType: e.target.value })}
                      >
                        <option value="monthly">Monthly</option>
                        <option value="one_time">One-Time</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Duration (days)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={assignForm.durationDays}
                        onChange={(e) => setAssignForm({ ...assignForm, durationDays: parseInt(e.target.value) || 30 })}
                      />
                    </div>
                    <div className="form-group">
                      <label>
                        <input
                          type="checkbox"
                          checked={assignForm.isGlobal}
                          onChange={(e) => setAssignForm({ ...assignForm, isGlobal: e.target.checked })}
                        />
                        {' '}Global (all cities)
                      </label>
                    </div>
                    {!assignForm.isGlobal && (
                      <div className="form-group full-width">
                        <label>Select Cities *</label>
                        <div className="city-checkboxes">
                          {cities.map(city => (
                            <label key={city._id} className="city-checkbox">
                              <input
                                type="checkbox"
                                checked={assignForm.cities.some(c => c.cityId === city._id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setAssignForm({
                                      ...assignForm,
                                      cities: [...assignForm.cities, { cityId: city._id, cityName: city.name }]
                                    });
                                  } else {
                                    setAssignForm({
                                      ...assignForm,
                                      cities: assignForm.cities.filter(c => c.cityId !== city._id)
                                    });
                                  }
                                }}
                              />
                              {city.name}
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="form-group full-width">
                      <label>Notes</label>
                      <textarea
                        className="form-control"
                        value={assignForm.notes}
                        onChange={(e) => setAssignForm({ ...assignForm, notes: e.target.value })}
                        rows={2}
                      />
                    </div>
                  </div>
                  <div className="form-actions">
                    <button type="submit" className="btn btn-primary">
                      Assign Addon
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setShowAssignForm(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Assignments List */}
            <div className="addon-card">
              <h2>Addon Assignments ({assignmentPagination.total})</h2>
              {assignments.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">📋</div>
                  <p>No assignments found.</p>
                </div>
              ) : (
                <div className="addon-list">
                  {assignments.map(assignment => (
                    <div key={assignment._id} className={`addon-item ${assignment.status}`}>
                      <div className="addon-main">
                        <div className="addon-header">
                          <span className="addon-icon">{assignment.addonId?.icon || getAddonIcon(assignment.addonSlug)}</span>
                          <span className="addon-name">{assignment.restaurantId?.name || 'Unknown Restaurant'}</span>
                          {getStatusBadge(assignment.status)}
                          <span className="badge badge-restaurant">{assignment.addonId?.displayName || assignment.addonSlug}</span>
                        </div>
                        <div className="addon-details">
                          <div className="detail-item">
                            <span className="detail-label">Type</span>
                            <span className="detail-value">{assignment.subscriptionType}</span>
                          </div>
                          <div className="detail-item">
                            <span className="detail-label">Scope</span>
                            <span className="detail-value">{assignment.isGlobal ? 'Global' : `${assignment.cities?.length || 0} cities`}</span>
                          </div>
                          {assignment.assignedByAdmin && (
                            <div className="detail-item">
                              <span className="detail-label">Assigned By</span>
                              <span className="detail-value">Admin</span>
                            </div>
                          )}
                          {assignment.isGlobal && assignment.globalSubscription && (
                            <div className="detail-item">
                              <span className="detail-label">Expires</span>
                              <span className="detail-value">
                                {new Date(assignment.globalSubscription.endDate).toLocaleDateString()}
                              </span>
                            </div>
                          )}
                        </div>
                        {!assignment.isGlobal && assignment.cities && assignment.cities.length > 0 && (
                          <div className="city-tags">
                            {assignment.cities.map((city, idx) => (
                              <span key={idx} className={`city-tag ${city.isActive && new Date(city.endDate) > new Date() ? 'active' : 'expired'}`}>
                                {city.cityName} - {new Date(city.endDate).toLocaleDateString()}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="addon-actions">
                        {assignment.status === 'active' && (
                          <>
                            <button className="btn btn-sm btn-success" onClick={() => handleRenewAssignment(assignment._id)}>
                              Renew
                            </button>
                            <button className="btn btn-sm btn-danger" onClick={() => handleCancelAssignment(assignment._id)}>
                              Cancel
                            </button>
                          </>
                        )}
                        {assignment.status === 'expired' && (
                          <button className="btn btn-sm btn-success" onClick={() => handleRenewAssignment(assignment._id)}>
                            Reactivate
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Pagination */}
              {assignmentPagination.pages > 1 && (
                <div className="pagination">
                  <button
                    className="btn btn-sm btn-secondary"
                    disabled={assignmentFilters.page <= 1}
                    onClick={() => setAssignmentFilters({ ...assignmentFilters, page: assignmentFilters.page - 1 })}
                  >
                    Previous
                  </button>
                  <span>Page {assignmentFilters.page} of {assignmentPagination.pages}</span>
                  <button
                    className="btn btn-sm btn-secondary"
                    disabled={assignmentFilters.page >= assignmentPagination.pages}
                    onClick={() => setAssignmentFilters({ ...assignmentFilters, page: assignmentFilters.page + 1 })}
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Requests Tab */}
        {activeTab === 'requests' && (
          <div className="tab-content">
            {/* Filters */}
            <div className="addon-card filters-card">
              <div className="filters-row">
                <select
                  value={requestFilters.status}
                  onChange={(e) => setRequestFilters({ ...requestFilters, status: e.target.value, page: 1 })}
                >
                  <option value="">All Statuses</option>
                  <option value="pending_approval">Pending Approval</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                  <option value="pending_payment">Pending Payment</option>
                </select>
                <select
                  value={requestFilters.addonSlug}
                  onChange={(e) => setRequestFilters({ ...requestFilters, addonSlug: e.target.value, page: 1 })}
                >
                  <option value="">All Addon Types</option>
                  {addons.map(addon => (
                    <option key={addon.slug} value={addon.slug}>{addon.displayName}</option>
                  ))}
                </select>
                <button className="btn btn-secondary" onClick={fetchAddonRequests}>
                  Refresh
                </button>
              </div>
            </div>

            {/* Requests List */}
            <div className="addon-card">
              <h2>Restaurant Addon Requests</h2>
              {addonRequests.length === 0 ? (
                <p className="no-data">No addon requests found.</p>
              ) : (
                <div className="requests-list">
                  {addonRequests.map(request => (
                    <div key={request._id} className={`request-item status-${request.status}`}>
                      <div className="request-header">
                        <div className="request-restaurant">
                          <h3>{request.restaurantId?.name || 'Unknown Restaurant'}</h3>
                          <span className="restaurant-email">{request.restaurantId?.email}</span>
                        </div>
                        <span className={`status-badge ${request.status}`}>
                          {request.status.replace(/_/g, ' ').toUpperCase()}
                        </span>
                      </div>

                      <div className="request-details">
                        <div className="detail-row">
                          <span className="detail-label">Addon:</span>
                          <span className="detail-value addon-name">{request.addonId?.displayName || request.addonSlug}</span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">Type:</span>
                          <span className="detail-value">{request.subscriptionType === 'monthly' ? 'Monthly Subscription' : 'One-Time Purchase'}</span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">Scope:</span>
                          <span className="detail-value">
                            {request.isGlobal ? 'Global (All Cities)' :
                              request.cities?.map(c => c.cityName).join(', ') || 'N/A'}
                          </span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">Amount:</span>
                          <span className="detail-value amount">₹{request.amount}</span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">Payment:</span>
                          <span className={`detail-value payment-status ${request.paymentStatus}`}>
                            {request.paymentStatus?.replace(/_/g, ' ').toUpperCase() || 'N/A'}
                          </span>
                        </div>
                        {request.cashfreeOrderId && (
                          <div className="detail-row">
                            <span className="detail-label">Order ID:</span>
                            <span className="detail-value order-id">{request.cashfreeOrderId}</span>
                          </div>
                        )}
                        <div className="detail-row">
                          <span className="detail-label">Requested:</span>
                          <span className="detail-value">{new Date(request.createdAt).toLocaleString()}</span>
                        </div>
                        {request.rejectionReason && (
                          <div className="detail-row rejection">
                            <span className="detail-label">Rejection Reason:</span>
                            <span className="detail-value">{request.rejectionReason}</span>
                          </div>
                        )}
                      </div>

                      {request.status === 'pending_approval' && (
                        <div className="request-actions">
                          <button
                            className="btn btn-success"
                            onClick={() => handleApproveRequest(request._id)}
                            disabled={processingRequest === request._id}
                          >
                            {processingRequest === request._id ? 'Processing...' : '✓ Approve'}
                          </button>
                          <button
                            className="btn btn-danger"
                            onClick={() => {
                              setSelectedRequest(request);
                              setShowRejectModal(true);
                            }}
                            disabled={processingRequest === request._id}
                          >
                            ✗ Reject
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Pagination */}
              {requestPagination.pages > 1 && (
                <div className="pagination">
                  <button
                    className="btn btn-sm btn-secondary"
                    disabled={requestFilters.page <= 1}
                    onClick={() => setRequestFilters({ ...requestFilters, page: requestFilters.page - 1 })}
                  >
                    Previous
                  </button>
                  <span>Page {requestFilters.page} of {requestPagination.pages}</span>
                  <button
                    className="btn btn-sm btn-secondary"
                    disabled={requestFilters.page >= requestPagination.pages}
                    onClick={() => setRequestFilters({ ...requestFilters, page: requestFilters.page + 1 })}
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Reject Request Modal */}
        {showRejectModal && selectedRequest && (
          <div className="modal-overlay" onClick={() => setShowRejectModal(false)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Reject Addon Request</h2>
                <button className="close-btn" onClick={() => setShowRejectModal(false)}>×</button>
              </div>
              <div className="modal-content">
                <p>
                  Rejecting request from <strong>{selectedRequest.restaurantId?.name}</strong> for{' '}
                  <strong>{selectedRequest.addonId?.displayName}</strong>
                </p>
                <div className="form-group">
                  <label>Rejection Reason *</label>
                  <textarea
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="Please provide a reason for rejection..."
                    rows={4}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowRejectModal(false);
                    setRejectReason('');
                    setSelectedRequest(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-danger"
                  onClick={handleRejectRequest}
                  disabled={!rejectReason.trim() || processingRequest}
                >
                  {processingRequest ? 'Rejecting...' : 'Reject Request'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Stats Tab */}
        {activeTab === 'stats' && (
          <div className="tab-content">
            {stats ? (
              <>
                {/* Totals */}
                <div className="addon-card">
                  <h2>Overview</h2>
                  <div className="stats-grid">
                    <div className="stat-item">
                      <div className="stat-icon total-icon">📦</div>
                      <div className="stat-info">
                        <div className="stat-label">Total Addon Types</div>
                        <div className="stat-value highlight">{stats.totals.totalAddons}</div>
                      </div>
                    </div>
                    <div className="stat-item">
                      <div className="stat-icon active-icon">✓</div>
                      <div className="stat-info">
                        <div className="stat-label">Active Assignments</div>
                        <div className="stat-value">{stats.totals.activeAssignments}</div>
                      </div>
                    </div>
                    <div className="stat-item">
                      <div className="stat-icon restaurant-icon">📋</div>
                      <div className="stat-info">
                        <div className="stat-label">Total Assignments</div>
                        <div className="stat-value">{stats.totals.totalAssignments}</div>
                      </div>
                    </div>
                    <div className="stat-item">
                      <div className="stat-icon expired-icon">⏰</div>
                      <div className="stat-info">
                        <div className="stat-label">Expiring Soon (7 days)</div>
                        <div className="stat-value">{stats.expiringSoon}</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* By Addon */}
                <div className="addon-card">
                  <h2>Statistics by Addon Type</h2>
                  <div className="addon-list">
                    {stats.byAddon.map(item => (
                      <div key={item.addon._id} className="addon-item stats-item">
                        <div className="addon-main">
                          <div className="addon-header">
                            <span className="addon-icon">{getAddonIcon(item.addon.slug)}</span>
                            <span className="addon-name">{item.addon.displayName}</span>
                            <span className="badge badge-scope">{item.addon.scope}</span>
                          </div>
                          <div className="stats-details">
                            <div className="stat-mini">
                              <span className="stat-mini-label">Total</span>
                              <span className="stat-mini-value">{item.totalAssignments}</span>
                            </div>
                            <div className="stat-mini">
                              <span className="stat-mini-label">Active</span>
                              <span className="stat-mini-value active">{item.activeAssignments}</span>
                            </div>
                            <div className="stat-mini">
                              <span className="stat-mini-label">Expired</span>
                              <span className="stat-mini-value expired">{item.expiredAssignments}</span>
                            </div>
                            <div className="stat-mini">
                              <span className="stat-mini-label">Cancelled</span>
                              <span className="stat-mini-value">{item.cancelledAssignments}</span>
                            </div>
                            <div className="stat-mini">
                              <span className="stat-mini-label">Revenue</span>
                              <span className="stat-mini-value revenue">₹{item.revenue}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              <div className="loading">Loading statistics...</div>
            )}
          </div>
        )}
      </div>
    </>
  );
}

export default AddonManagement;
